import {
  AdminAffiliateLinkedClient,
  AdminAffiliateLinkedClientsResponse,
  AdminAffiliateResponse,
  AdminApproveAffiliateResponse,
  AdminSuspendAffiliateResponse,
  AdminUpdateAffiliateSettingsResponse,
  AffiliateAccountStatus,
  AffiliateAccountWithUser,
} from '@fsp/types';
import BadgeTwoTone from '@mui/icons-material/BadgeTwoTone';
import DeleteIcon from '@mui/icons-material/Delete';
import HandshakeTwoTone from '@mui/icons-material/HandshakeTwoTone';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  InputAdornment,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import BreadCrumb from '../../../components/BreadCrumb';
import ConfirmDialog from '../../../components/ConfirmDialog';
import OrganisationChip from '../../../components/OrganisationChip';
import { apiClient } from '../../../services/apiClient';
import { formatDate, DateFormatOptions } from '@fsp/shared';
import { getSubscriptionStatusColor } from '../../../utils/getSubscriptionStatusColor';

const getStatusColor = (
  status: AffiliateAccountStatus
): 'warning' | 'success' | 'error' | 'default' => {
  switch (status) {
    case 'PENDING':
      return 'warning';
    case 'APPROVED':
      return 'success';
    case 'SUSPENDED':
      return 'error';
    default:
      return 'default';
  }
};

const getAvatarColor = (status: AffiliateAccountStatus) => {
  switch (status) {
    case 'APPROVED':
      return 'success.main';
    case 'SUSPENDED':
      return 'error.main';
    default:
      return 'warning.main';
  }
};

type DialogAction = 'approve' | 'reject' | 'suspend' | 'reinstate' | null;

const DIALOG_CONFIG: Record<
  Exclude<DialogAction, null>,
  { title: string; message: string; confirmLabel: string; confirmColor: 'primary' | 'error' | 'warning' | 'success' }
> = {
  approve: {
    title: 'Approve Affiliate',
    message:
      'This will approve the affiliate account, create a Stripe coupon and promotion code using their referral code, and send them an approval email. Are you sure?',
    confirmLabel: 'Approve',
    confirmColor: 'primary',
  },
  reject: {
    title: 'Reject Affiliate',
    message:
      'Are you sure you want to reject this affiliate application? This action cannot be easily undone.',
    confirmLabel: 'Reject',
    confirmColor: 'error',
  },
  suspend: {
    title: 'Suspend Affiliate',
    message:
      'This will suspend the affiliate account and deactivate their Stripe promotion code. They will no longer be able to refer new clients. Are you sure?',
    confirmLabel: 'Suspend',
    confirmColor: 'error',
  },
  reinstate: {
    title: 'Reinstate Affiliate',
    message:
      'This will reinstate the suspended affiliate account. Are you sure you want to reinstate this affiliate?',
    confirmLabel: 'Reinstate',
    confirmColor: 'success',
  },
};

const InfoItem = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500 }}>
      {value ?? 'N/A'}
    </Typography>
  </Box>
);

export default function AuditorView() {
  const { auditorAccountId } = useParams<{ auditorAccountId: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [affiliate, setAffiliate] = useState<AffiliateAccountWithUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogAction, setDialogAction] = useState<DialogAction>(null);

  // Editable fields state
  const [isEditingDiscount, setIsEditingDiscount] = useState(false);
  const [isEditingCommission, setIsEditingCommission] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [editableFields, setEditableFields] = useState({
    referralCode: '',
    commissionRate: 0,
    discountRate: 0,
    discountDuration: 0,
  });

  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Linked clients state
  const [linkedClients, setLinkedClients] = useState<AdminAffiliateLinkedClient[]>([]);
  const [clientsLoading, setClientsLoading] = useState(true);

  useEffect(() => {
    if (auditorAccountId) {
      fetchAffiliate();
      fetchLinkedClients();
    }
  }, [auditorAccountId]);

  // Sync editable fields when affiliate data loads or changes
  useEffect(() => {
    if (affiliate) {
      setEditableFields({
        referralCode: affiliate.referralCode,
        commissionRate: affiliate.commissionRate,
        discountRate: affiliate.discountRate,
        discountDuration: affiliate.discountDuration,
      });
    }
  }, [affiliate]);

  const fetchAffiliate = async () => {
    if (!auditorAccountId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/admin/auditors/${auditorAccountId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch affiliate details');
      }
      const data: AdminAffiliateResponse = await response.json();
      setAffiliate(data.affiliate);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching affiliate:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLinkedClients = async () => {
    if (!auditorAccountId) return;
    setClientsLoading(true);
    try {
      const response = await apiClient.get(`/admin/auditors/${auditorAccountId}/clients`);
      if (!response.ok) {
        throw new Error('Failed to fetch linked clients');
      }
      const data: AdminAffiliateLinkedClientsResponse = await response.json();
      setLinkedClients(data.clients);
    } catch (err) {
      console.error('Error fetching linked clients:', err);
    } finally {
      setClientsLoading(false);
    }
  };

  const hasDiscountChanges = affiliate
    ? editableFields.referralCode !== affiliate.referralCode ||
      editableFields.discountRate !== affiliate.discountRate ||
      editableFields.discountDuration !== affiliate.discountDuration
    : false;

  const hasCommissionChanges = affiliate
    ? editableFields.commissionRate !== affiliate.commissionRate
    : false;

  const handleSaveSettings = async (section: 'discount' | 'commission') => {
    if (!auditorAccountId) return;
    setSaveLoading(true);
    try {
      const response = await apiClient.put(
        `/admin/auditors/${auditorAccountId}/settings`,
        editableFields
      );
      if (!response.ok) {
        const err: any = await response.json();
        throw new Error(err?.message || 'Failed to save settings');
      }
      const data: AdminUpdateAffiliateSettingsResponse = await response.json();
      setAffiliate((prev) => (prev ? { ...prev, ...data.affiliate } : prev));
      if (section === 'discount') {
        setIsEditingDiscount(false);
      } else {
        setIsEditingCommission(false);
      }
      enqueueSnackbar('Affiliate settings saved successfully.', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Failed to save settings', {
        variant: 'error',
      });
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!auditorAccountId) return;
    setIsDeleting(true);
    setDeleteDialogOpen(false);
    try {
      const response = await apiClient.delete(`/admin/auditors/${auditorAccountId}`);
      if (!response.ok) {
        const err: any = await response.json();
        throw new Error(err?.message || 'Failed to delete affiliate');
      }
      enqueueSnackbar('Affiliate account deleted successfully.', { variant: 'success' });
      navigate('/app/auditors');
    } catch (err) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Failed to delete affiliate', {
        variant: 'error',
      });
      setIsDeleting(false);
    }
  };

  const handleConfirm = async () => {
    if (!auditorAccountId || !dialogAction) return;

    setActionLoading(true);
    setDialogAction(null);

    try {
      let response: Response;

      if (dialogAction === 'approve') {
        response = await apiClient.put(`/admin/auditors/${auditorAccountId}/approve`);
        if (!response.ok) {
          const err: any = await response.json();
          throw new Error(err?.message || 'Failed to approve affiliate');
        }
        const data: AdminApproveAffiliateResponse = await response.json();
        setAffiliate((prev) => (prev ? { ...prev, ...data.affiliate } : prev));
        enqueueSnackbar('Affiliate approved successfully. Stripe coupon created and email sent.', {
          variant: 'success',
        });
      } else if (dialogAction === 'suspend') {
        response = await apiClient.put(`/admin/auditors/${auditorAccountId}/suspend`);
        if (!response.ok) {
          const err: any = await response.json();
          throw new Error(err?.message || 'Failed to suspend affiliate');
        }
        const data: AdminSuspendAffiliateResponse = await response.json();
        setAffiliate((prev) => (prev ? { ...prev, ...data.affiliate } : prev));
        enqueueSnackbar('Affiliate suspended and promotion code deactivated.', {
          variant: 'success',
        });
      } else if (dialogAction === 'reinstate') {
        response = await apiClient.put(`/admin/auditors/${auditorAccountId}/approve`);
        if (!response.ok) {
          const err: any = await response.json();
          throw new Error(err?.message || 'Failed to reinstate affiliate');
        }
        const data: AdminApproveAffiliateResponse = await response.json();
        setAffiliate((prev) => (prev ? { ...prev, ...data.affiliate } : prev));
        enqueueSnackbar('Affiliate reinstated successfully.', { variant: 'success' });
      } else if (dialogAction === 'reject') {
        response = await apiClient.put(`/admin/auditors/${auditorAccountId}/suspend`);
        if (!response.ok) {
          const err: any = await response.json();
          throw new Error(err?.message || 'Failed to reject affiliate');
        }
        const data: AdminSuspendAffiliateResponse = await response.json();
        setAffiliate((prev) => (prev ? { ...prev, ...data.affiliate } : prev));
        enqueueSnackbar('Affiliate application rejected.', { variant: 'success' });
      }
    } catch (err) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Action failed', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !affiliate) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <BreadCrumb />
          <Typography variant="h5" color="error">
            {error || 'Affiliate not found'}
          </Typography>
        </Box>
      </Container>
    );
  }

  const fullName = `${affiliate.user.firstName} ${affiliate.user.lastName}`;
  const dialogConfig = dialogAction ? DIALOG_CONFIG[dialogAction] : null;
  const canEdit = affiliate.status !== 'SUSPENDED';

  return (
    <Container maxWidth="xl">
      <BreadCrumb customLabel={fullName} />

      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ width: 64, height: 64, bgcolor: getAvatarColor(affiliate.status) }}>
            <BadgeTwoTone sx={{ fontSize: 32, color: 'white' }} />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              {fullName}
            </Typography>
            {affiliate.businessName && (
              <Typography variant="body1" color="text.secondary">
                {affiliate.businessName}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={affiliate.status}
            color={getStatusColor(affiliate.status)}
            sx={{ height: 32, fontSize: '0.875rem' }}
          />

          {/* Action buttons based on status */}
          {affiliate.status === 'PENDING' && (
            <>
              <Button
                variant="contained"
                color="primary"
                disabled={actionLoading}
                onClick={() => setDialogAction('approve')}
              >
                Approve
              </Button>
              <Button
                variant="outlined"
                color="error"
                disabled={actionLoading}
                onClick={() => setDialogAction('reject')}
              >
                Reject
              </Button>
            </>
          )}

          {affiliate.status === 'APPROVED' && (
            <Button
              variant="outlined"
              color="error"
              disabled={actionLoading}
              onClick={() => setDialogAction('suspend')}
            >
              {actionLoading ? <CircularProgress size={20} /> : 'Suspend'}
            </Button>
          )}

          {affiliate.status === 'SUSPENDED' && (
            <>
              <Button
                variant="contained"
                color="primary"
                disabled={actionLoading || isDeleting}
                onClick={() => setDialogAction('reinstate')}
              >
                {actionLoading ? <CircularProgress size={20} /> : 'Reinstate'}
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                disabled={actionLoading || isDeleting}
                onClick={() => setDeleteDialogOpen(true)}
              >
                {isDeleting ? <CircularProgress size={20} /> : 'Delete'}
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Contact Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            Contact Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <InfoItem label="Email" value={affiliate.user.email} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <InfoItem label="Phone" value={affiliate.phone} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <InfoItem
                label="Website"
                value={
                  affiliate.website ? (
                    <Link
                      href={affiliate.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      {affiliate.website}
                      <OpenInNewIcon sx={{ fontSize: 14 }} />
                    </Link>
                  ) : null
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <InfoItem
                label="Registered"
                value={formatDate(affiliate.createdAt, DateFormatOptions.DATEANDTIME)}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Referral & Stripe Details */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Referral & Stripe Details
            </Typography>
            {canEdit && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                {isEditingDiscount ? (
                  <>
                    <Button
                      size="small"
                      onClick={() => {
                        setIsEditingDiscount(false);
                        setEditableFields((prev) => ({
                          ...prev,
                          referralCode: affiliate.referralCode,
                          discountRate: affiliate.discountRate,
                          discountDuration: affiliate.discountDuration,
                        }));
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={!hasDiscountChanges || saveLoading}
                      onClick={() => handleSaveSettings('discount')}
                    >
                      {saveLoading ? <CircularProgress size={20} /> : 'Save'}
                    </Button>
                  </>
                ) : (
                  <Button size="small" variant="outlined" onClick={() => setIsEditingDiscount(true)}>
                    Edit
                  </Button>
                )}
              </Box>
            )}
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              {isEditingDiscount && canEdit ? (
                <TextField
                  label="Referral Code"
                  value={editableFields.referralCode}
                  onChange={(e) =>
                    setEditableFields((prev) => ({
                      ...prev,
                      referralCode: e.target.value.toUpperCase(),
                    }))
                  }
                  fullWidth
                  size="small"
                  inputProps={{ style: { fontFamily: 'monospace' } }}
                />
              ) : (
                <InfoItem
                  label="Referral Code"
                  value={
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '1rem' }}
                    >
                      {affiliate.referralCode}
                    </Typography>
                  }
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {isEditingDiscount && canEdit ? (
                <TextField
                  label="Discount Rate"
                  value={Math.round(editableFields.discountRate * 100)}
                  onChange={(e) =>
                    setEditableFields((prev) => ({
                      ...prev,
                      discountRate: Number(e.target.value) / 100,
                    }))
                  }
                  fullWidth
                  size="small"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                />
              ) : (
                <InfoItem
                  label="Discount Rate"
                  value={`${(affiliate.discountRate * 100).toFixed(0)}%`}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {isEditingDiscount && canEdit ? (
                <TextField
                  label="Discount Duration"
                  value={editableFields.discountDuration}
                  onChange={(e) =>
                    setEditableFields((prev) => ({
                      ...prev,
                      discountDuration: Number(e.target.value),
                    }))
                  }
                  fullWidth
                  size="small"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">months</InputAdornment>,
                  }}
                />
              ) : (
                <InfoItem
                  label="Discount Duration"
                  value={`${affiliate.discountDuration} months`}
                />
              )}
            </Grid>
            {affiliate.stripeCouponId && (
              <Grid item xs={12} sm={6} md={3}>
                <InfoItem
                  label="Stripe Coupon ID"
                  value={
                    <Link
                      href={`https://dashboard.stripe.com/coupons/${affiliate.stripeCouponId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                      }}
                    >
                      {affiliate.stripeCouponId}
                      <OpenInNewIcon sx={{ fontSize: 14 }} />
                    </Link>
                  }
                />
              </Grid>
            )}
            {affiliate.stripePromotionCodeId && (
              <Grid item xs={12} sm={6} md={3}>
                <InfoItem
                  label="Stripe Promotion Code ID"
                  value={
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 500 }}
                    >
                      {affiliate.stripePromotionCodeId}
                    </Typography>
                  }
                />
              </Grid>
            )}
            {affiliate.status === 'PENDING' && !isEditingDiscount && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Stripe coupon and promotion code will be created upon approval.
                </Typography>
              </Grid>
            )}
            {affiliate.status === 'APPROVED' && isEditingDiscount && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Note: Changing these values will not retroactively update the existing Stripe
                  coupon or promotion code. Changes will only apply to future approvals.
                </Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Commission Stats */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Commission Stats
            </Typography>
            {canEdit && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                {isEditingCommission ? (
                  <>
                    <Button
                      size="small"
                      onClick={() => {
                        setIsEditingCommission(false);
                        setEditableFields((prev) => ({
                          ...prev,
                          commissionRate: affiliate.commissionRate,
                        }));
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={!hasCommissionChanges || saveLoading}
                      onClick={() => handleSaveSettings('commission')}
                    >
                      {saveLoading ? <CircularProgress size={20} /> : 'Save'}
                    </Button>
                  </>
                ) : (
                  <Button size="small" variant="outlined" onClick={() => setIsEditingCommission(true)}>
                    Edit
                  </Button>
                )}
              </Box>
            )}
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={3}>
              <Stack spacing={0.5} alignItems="flex-start">
                <Typography variant="caption" color="text.secondary">
                  Referred Clients
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {affiliate.referralClientCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Signed up via promo code
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Stack spacing={0.5} alignItems="flex-start">
                <Typography variant="caption" color="text.secondary">
                  Connected Clients
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                  {affiliate.clientCount - affiliate.referralClientCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Manually linked
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={3}>
              {isEditingCommission && canEdit ? (
                <TextField
                  label="Commission Rate"
                  value={Math.round(editableFields.commissionRate * 100)}
                  onChange={(e) =>
                    setEditableFields((prev) => ({
                      ...prev,
                      commissionRate: Number(e.target.value) / 100,
                    }))
                  }
                  fullWidth
                  size="small"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                />
              ) : (
                <Stack spacing={0.5} alignItems="flex-start">
                  <Typography variant="caption" color="text.secondary">
                    Commission Rate
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {(affiliate.commissionRate * 100).toFixed(0)}%
                  </Typography>
                </Stack>
              )}
            </Grid>
            <Grid item xs={12} sm={3}>
              <Stack spacing={0.5} alignItems="flex-start">
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={affiliate.status}
                  color={getStatusColor(affiliate.status)}
                  sx={{ mt: 0.5 }}
                />
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Connected Organisations */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            Connected Organisations
          </Typography>
          {clientsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : linkedClients.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No organisations connected to this affiliate.
            </Typography>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Organisation</TableCell>
                      <TableCell>Subscription Status</TableCell>
                      <TableCell align="center">Affiliate</TableCell>
                      <TableCell>Linked</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {linkedClients.map((client) => (
                      <TableRow
                        key={client.organisationId}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'action.hover',
                            cursor: 'pointer',
                          },
                        }}
                        onClick={() => navigate(`/app/organisations/${client.organisationId}`)}
                      >
                        <TableCell>
                          <OrganisationChip
                            organisation={{ id: client.organisationId, name: client.orgName }}
                            clickable={false}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={client.subscriptionStatus}
                            color={getSubscriptionStatusColor(client.subscriptionStatus as any)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={client.linkType === 'REFERRAL' ? 'Referral' : 'Manual link'}>
                            <HandshakeTwoTone
                              fontSize="small"
                              color={client.linkType === 'REFERRAL' ? 'success' : 'action'}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          {formatDate(client.linkedAt, DateFormatOptions.DATEONLY)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1.5, display: 'block' }}
              >
                {linkedClients.length} organisation{linkedClients.length !== 1 ? 's' : ''}
              </Typography>
            </>
          )}
        </CardContent>
      </Card>

      {/* Confirm Dialog */}
      {dialogConfig && (
        <ConfirmDialog
          open={!!dialogAction}
          title={dialogConfig.title}
          message={dialogConfig.message}
          confirmLabel={dialogConfig.confirmLabel}
          confirmColor={dialogConfig.confirmColor}
          onConfirm={handleConfirm}
          onCancel={() => setDialogAction(null)}
        />
      )}

      {/* Delete Confirm Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Affiliate"
        message="This will permanently delete this suspended affiliate account and all associated client links. This action cannot be undone. Are you sure?"
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </Container>
  );
}
